//eslint-disable-next-line no-unused-vars
import {
  Box,
  Button,
  Checkbox,
  Typography,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
  IconButton,
  Collapse,
} from "@mui/material";
import { memo, useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { formatProduct } from "../../../utils/utilfunction";
import { exchangeSpecificStringifiedNumber } from "../../../services/exchanges";
import { stringifyNumberToIndianLocale } from "../../../services/utilities";
import ConfirmationModal from "../../Common/ConfimationModel";
import MobileView from "./MobileView";
import GenericTable from "../../Common/GenericTable";
import { positionsConfig } from "../../../config/tableConfigs";
import { usePositions } from "../../../hooks/usePositions";
import PositionDetailsDrawer from './PositionDetailsDrawer';
import CommonMessages from '../../Common/NoDataMessages';
import NoOrdersIllustration from '../../Common/NoOrdersIllustration';
import useBalance from "./useBalance";
import { fetchFundsView } from "../../../store/actions/funds";
import { fetchPositionsAction } from "../../../store/actions/portfolio";


function Positions() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedPositionForDetails, setSelectedPositionForDetails] = useState(null);
  const [conversionMode, setConversionMode] = useState(false);
  const [quantity, setQuantity] = useState("");
  const [error, setError] = useState("");
  const [pnlDetailsOpen, setPnlDetailsOpen] = useState(false);

  // Use the balance hook to get consistent PnL values across the app
  const balanceData = useBalance();

  const {
    // total,
    totalInvestedValue,
    totalPnlPercentage,
    isAllSelected,
    selectedPosition,
    openConfirmationBox,
    positions,
    positionType,
    conversionDrawerOpen,
    selectedPositionForConversion,
    handleCheckbox,
    handleSquareOffClick,
    setOpenConfirmationBox,
    executeSquareOff,
    selectAllHandlers,
    selectAllTouchStates,
    handleConvertPosition,
    handleCloseConversionDrawer,
    handleAddMore,
    dispatch,
  } = usePositions();
  
  // Fetch funds data when component mounts
  useEffect(() => {
    dispatch(fetchFundsView());
    dispatch(fetchPositionsAction("historical"));
  }, [dispatch]);
  
  const positionMetrics = useSelector(state => state.portfolio.positionMetrics || {});
  console.log("positionMetrics", positionMetrics);
  
  // Always filter out positions with zero quantity
  const filteredPositions = positions?.filter(position => position.net_quantity !== 0);

  // Function to determine target product type
  const getTargetProductType = (currentProduct, exchange) => {
    if (!currentProduct || !exchange) {
      return null;
    }

    if (currentProduct === 'MIS') {
      return exchange === 'NSE' ? 'CNC' : 'NRML';
    } else {
      return 'MIS';
    }
  };

  // Handle row click to open details drawer
  const handleRowClick = useCallback((position) => {
    setSelectedPositionForDetails(position);
    setConversionMode(false);
    setQuantity(String(Math.abs(position.net_quantity)));
    setError("");
  }, []);

  // Close details drawer
  const handleCloseDetailsDrawer = useCallback(() => {
    setSelectedPositionForDetails(null);
    setConversionMode(false);
  }, []);

  // Handle quantity change
  const handleQuantityChange = useCallback((e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setQuantity(value);
      setError("");
      
      if (selectedPositionForDetails && Number(value) > Math.abs(selectedPositionForDetails.net_quantity)) {
        setError("Quantity cannot exceed position size");
      }
    }
  }, [selectedPositionForDetails]);

  // Handle convert
  const handleConvert = useCallback(() => {
    if (!selectedPositionForDetails) {
      setError("Position data is missing");
      return;
    }

    if (!quantity) {
      setError("Please enter a quantity");
      return;
    }
    
    if (Number(quantity) > Math.abs(selectedPositionForDetails.net_quantity)) {
      setError("Quantity cannot exceed position size");
      return;
    }

    const targetProduct = getTargetProductType(selectedPositionForDetails.product, selectedPositionForDetails.exchange);
    
    // Execute conversion
    handleConvertPosition(selectedPositionForDetails, Number(quantity), targetProduct);
    
    // Close drawer after conversion
    setSelectedPositionForDetails(null);
    setConversionMode(false);
  }, [selectedPositionForDetails, quantity, handleConvertPosition]);

  // Add touch handling to prevent parent scroll
  const handleTouchMove = (event) => {
    const element = event.currentTarget;
    const isAtTop = element.scrollTop === 0;
    const isAtBottom =
      element.scrollHeight - element.scrollTop === element.clientHeight;

    if (
      (isAtTop && event.touches[0].clientY > 0) ||
      (isAtBottom && event.touches[0].clientY < 0)
    ) {
      event.preventDefault();
    }
  };

  // Toggle PnL details visibility
  const togglePnlDetails = () => {
    setPnlDetailsOpen(!pnlDetailsOpen);
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Fixed Header Controls */}
      <Box
        sx={{
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: "sticky",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
        }}
      >
        {/* Position Type Buttons and Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            mb: 1.5,
            mt: 2,
            px: 1,
            flexWrap: "nowrap",
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Button
            size="small"
            sx={{
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              height: 32,
              textTransform: "none",
              color:
                positionType === "historical"
                  ? theme.palette.primary.contrastText
                  : theme.palette.text.primary,
              backgroundColor:
                positionType === "historical"
                  ? theme.palette.primary.main
                  : "transparent",
              borderRadius: 1,
              whiteSpace: "nowrap",
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
            variant={positionType === "historical" ? "contained" : "outlined"}
            onClick={() =>
              dispatch({
                type: "SET_POSITION_TYPE",
                payload: "historical",
              })
            }
          >
            Netwise
          </Button>

          {isMobile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Box
                {...selectAllHandlers}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  backgroundColor:
                    selectAllTouchStates.isActive &&
                    !selectAllTouchStates.isTouchMoved
                      ? "action.hover"
                      : "transparent",
                  transition: "background-color 0.15s ease",
                  borderRadius: 1,
                  px: 0.5,
                  "&:active": {
                    backgroundColor: !selectAllTouchStates.isTouchMoved
                      ? "action.hover"
                      : "transparent",
                  },
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  name="allSelect"
                  size="small"
                  sx={{
                    p: 0.5,
                    pointerEvents: "none",
                    "& .MuiSvgIcon-root": {
                      fontSize: "1.25rem",
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                  }}
                >
                  Select All
                </Typography>
              </Box>
            </Box>
          )}
          {selectedPosition.length > 0 && (
            <Box
              sx={{
                display: "flex",
                ml: "auto",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSquareOffClick}
                sx={{
                  minWidth: "auto",
                  px: 1.5,
                  py: 0.5,
                  height: 32,
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  whiteSpace: "nowrap",
                  WebkitTapHighlightColor: "transparent",
                  backgroundColor: "primary.main",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                  "&:active": {
                    backgroundColor: "primary.dark",
                    transform: "scale(0.98)",
                  },
                }}
              >
                Square Off ({selectedPosition.length})
              </Button>
            </Box>
          )}
        </Box>

        {/* Updated Total P&L Card */}
        <Box
          sx={{
            p: 1.5,
            mb: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            bgcolor: theme.palette.background.default,
          }}
        >
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <Typography variant="subtitle2" color="text.secondary">
              Unrealized P&L
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: balanceData.isPositive.totalPnl ? theme.palette.success.main : theme.palette.error.main,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {balanceData.isPositive.totalPnl ? '+' : ''}
              ₹{stringifyNumberToIndianLocale(Math.abs(balanceData.totalPnl), 2)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {filteredPositions.length === 0 ? (
        <CommonMessages
          icon={<NoOrdersIllustration />}
          messageHead="No positions"
          messageBody="You have no open positions at the moment"
        />
      ) : isMobile ? (
        <Box
          sx={{
            flex: 1,
            position: "relative",
            overflow: "hidden",
            height: "calc(100% - 140px)",
            touchAction: "none",
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Box
            onTouchMove={handleTouchMove}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
              msOverflowStyle: "none",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              overscrollBehavior: "contain",
              touchAction: "pan-y",
              backgroundColor: theme.palette.background.default,
            }}
          >
            <MobileView
              positions={filteredPositions}
              selectedPosition={selectedPosition}
              handleCheckbox={handleCheckbox}
              formatProduct={formatProduct}
              exchangeSpecificStringifiedNumber={exchangeSpecificStringifiedNumber}
              onRowClick={handleRowClick}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            height: "calc(100vh - 250px)",
            overflow: "auto",
            backgroundColor: theme.palette.background.default,
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.divider,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          }}
        >
          <GenericTable
            data={filteredPositions}
            config={positionsConfig(isMobile, exchangeSpecificStringifiedNumber)}
            selectedItems={selectedPosition}
            handleCheckbox={handleCheckbox}
            showCheckbox={true}
            isAllSelected={isAllSelected}
            isMobile={isMobile}
            onRowClick={handleRowClick}
          />
        </Box>
      )}

      <ConfirmationModal
        open={openConfirmationBox}
        setOpen={setOpenConfirmationBox}
        heading="Are you sure?"
        message="All selected positions will be squared off at market price"
        callFunc={executeSquareOff}
      />

      <PositionDetailsDrawer
        open={!!selectedPositionForDetails}
        onClose={handleCloseDetailsDrawer}
        position={selectedPositionForDetails}
        conversionMode={conversionMode}
        setConversionMode={setConversionMode}
        quantity={quantity}
        error={error}
        handleQuantityChange={handleQuantityChange}
        handleConvert={handleConvert}
        getTargetProductType={getTargetProductType}
        onAddMore={handleAddMore}
      />
      
    </Box>
  );
}

export default memo(Positions);
